/**
 * `/sites/$siteId/content` — standalone ContentItems list (issue #173).
 *
 * Renders the same `ContentItemsTable` body that the Content tab on
 * `SiteDetailPage` uses, wrapped in a page-level shell (breadcrumb +
 * heading). The duplicated entry point exists so the operator can deep-
 * link straight to the ContentItems surface (e.g. from a follow-on AI
 * Studio "view all blocks" link) without first landing on the site
 * detail page and switching tabs.
 *
 * Layout — single-column with a breadcrumb, the site header, and the
 * shared `ContentItemsTable` below.
 */

import type { ReactElement } from "react";
import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { Link, useParams } from "@tanstack/react-router";
import { useSite } from "../hooks/useSites";
import { ContentItemsTable } from "../features/content/ContentItemsTable";

export function ContentItemsListPage(): ReactElement {
  // Route id is `/authenticated/sites/$siteId/content` because the
  // route lives beneath the pathless `authenticated` layout route.
  const { siteId } = useParams({ from: "/authenticated/sites/$siteId/content" });
  const site = useSite(siteId);

  if (site.isPending) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}
        data-testid="content-items-list-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (site.isError) {
    return (
      <Alert severity="error" data-testid="content-items-list-error">
        {site.error.message}
      </Alert>
    );
  }

  if (!site.data) {
    return (
      <Alert severity="warning" data-testid="content-items-list-missing">
        Site not found.
      </Alert>
    );
  }

  return (
    <Stack spacing={3} data-testid="content-items-list-page">
      <Box>
        <Typography variant="body2" color="text.secondary">
          <Link to="/sites">Sites</Link> /{" "}
          <Link to="/sites/$siteId" params={{ siteId }}>
            {site.data.name}
          </Link>{" "}
          / Content
        </Typography>
        <Typography variant="h2" sx={{ mt: 1 }} data-testid="content-items-list-heading">
          Content items
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Blocks and other reusable content for this site. Edit a row to manage its versions (draft
          / published / archived).
        </Typography>
      </Box>
      <ContentItemsTable siteId={siteId} />
    </Stack>
  );
}
